import React                from "react";
import PropTypes            from "prop-types";
import Action               from "Dashboard/Core/Action";
import Store                from "Dashboard/Core/Store";
import Utils                from "Dashboard/Utils/Utils";
import useList              from "Dashboard/Hooks/List";
import Commons              from "Utils/Commons";

// Components
import TrainingEdit         from "./TrainingEdit";

// Dashboard
import Table                from "Dashboard/Components/Table/Table";
import TableHead            from "Dashboard/Components/Table/TableHead";
import TableBody            from "Dashboard/Components/Table/TableBody";
import TableRow             from "Dashboard/Components/Table/TableRow";
import TableHeader          from "Dashboard/Components/Table/TableHeader";
import TableCell            from "Dashboard/Components/Table/TableCell";
import TablePaging          from "Dashboard/Components/Table/TablePaging";
import TableActionList      from "Dashboard/Components/Table/TableActionList";
import TableAction          from "Dashboard/Components/Table/TableAction";
import DeleteDialog         from "Dashboard/Components/Dialogs/DeleteDialog";



/**
 * The Training Content
 * @param {Object} props
 * @returns {React.ReactElement}
 */
function TrainingContent(props) {
    const { type, isCreating, setCreating } = props;
    const { fetch, load } = useList("training", type);

    const activityID = Commons.useActivityID();

    const data = Store.useState("training");
    const { loading, canEdit, list, total, sort } = data;

    const { deleteElem } = Store.useAction("training");

    // The Current State
    const [ action, elemID, startAction, endAction ] = Action.useActionID();


    // Open the Create Dialog
    React.useEffect(() => {
        if (isCreating) {
            startAction("CREATE");
        }
    }, [ isCreating ]);

    // Handles the Edit Submit
    const handleEdit = () => {
        handleClose();
        fetch();
    };

    // Handles the Delete Submit
    const handleDelete = async () => {
        handleClose();
        const result = await deleteElem(elemID);
        if (result.success) {
            fetch();
        }
    };

    // Handles the Close
    const handleClose = () => {
        setCreating(false);
        endAction();
    };


    // Do the Render
    const elemName = Utils.getValue(list, "trainingID", elemID, "name");

    return <>
        <Table
            fetch={load}
            sort={sort}
            none="TRAININGS_NONE_AVAILABLE"
            isLoading={loading}
            hasTabs
        >
            <TableHead>
                <TableHeader field="name"                  message="GENERAL_NAME"                   isTitle bigMobile />
                <TableHeader field="trainingWhiteListName" message="TRAININGS_WHITE_LISTS_SINGULAR" />
                <TableHeader field="fromTime"              message="GENERAL_FROM_DATE"              maxWidth="120" />
                <TableHeader field="toTime"                message="GENERAL_TO_DATE"                maxWidth="120" />
                <TableHeader field="totalSubscriptions"    message="SUBSCRIPTIONS_NAME"             maxWidth="100" align="center" />
                <TableHeader field="status"                message="GENERAL_STATUS"                 maxWidth="100" align="center" />
            </TableHead>
            <TableBody>
                {list.map((elem) => <TableRow key={elem.trainingID} elemID={elem.trainingID}>
                    <TableCell message={elem.name}                  />
                    <TableCell message={elem.trainingWhiteListName} />
                    <TableCell message={elem.fromTimeDate}          />
                    <TableCell message={elem.toTimeDate}            />
                    <TableCell message={elem.subscriptionText}      />
                    <TableCell message={elem.statusName}            textColor={elem.statusColor} />
                </TableRow>)}
            </TableBody>
            <TablePaging total={total} />
            <TableActionList onAction={startAction} canEdit={canEdit}>
                <TableAction action="VIEW"   message="TRAININGS_VIEW_TITLE"   navigate />
                <TableAction action="EDIT"   message="TRAININGS_EDIT_TITLE"   />
                <TableAction action="COPY"   message="TRAININGS_COPY_TITLE"   />
                <TableAction action="DELETE" message="TRAININGS_DELETE_TITLE" />
            </TableActionList>
        </Table>

        <TrainingEdit
            open={action.isVCCE}
            isCopy={action.isCopy}
            elemID={elemID}
            activityID={activityID}
            onSubmit={handleEdit}
            onClose={handleClose}
        />
        <DeleteDialog
            open={action.isDelete}
            title="TRAININGS_DELETE_TITLE"
            message="TRAININGS_DELETE_TEXT"
            content={elemName}
            onSubmit={handleDelete}
            onClose={handleClose}
        />
    </>;
}

/**
 * The Property Types
 * @typedef {Object} propTypes
 */
TrainingContent.propTypes = {
    type        : PropTypes.string.isRequired,
    isCreating  : PropTypes.bool.isRequired,
    setCreating : PropTypes.func.isRequired,
};

export default TrainingContent;
